// Searchable Select component - allows typing to filter options
import React, { useState, useRef, useEffect } from 'react';

const SearchableSelect = ({ 
  placeholder = "Search and select...",
  selectedKeys,
  onSelectionChange,
  options = [], // Array of {value, label} or just strings
  className = '',
  ...props 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize selected value from selectedKeys
  useEffect(() => {
    if (selectedKeys && selectedKeys.size > 0) {
      const selectedValue = Array.from(selectedKeys)[0];
      const option = options.find(opt => {
        const value = typeof opt === 'string' ? opt : (opt.value || opt.make || opt);
        return value === selectedValue;
      });
      if (option) {
        const label = typeof option === 'string' ? option : (option.label || option.make || option.value || option);
        setSelected({ value: selectedValue, label });
        setSearchQuery(label);
      }
    }
  }, [selectedKeys, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search query
  const filteredOptions = options.filter(option => {
    const label = typeof option === 'string' ? option : (option.label || option.make || option.value || option);
    const searchLower = searchQuery.toLowerCase();
    return label.toLowerCase().includes(searchLower);
  });

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsOpen(true);
    // Clear selection if user is typing
    if (selected && value !== selected.label) {
      setSelected(null);
      if (onSelectionChange) {
        onSelectionChange(new Set());
      }
    }
  };

  const handleSelect = (option) => {
    const value = typeof option === 'string' ? option : (option.value || option.make || option);
    const label = typeof option === 'string' ? option : (option.label || option.make || option.value || option);
    
    setSelected({ value, label });
    setSearchQuery(label);
    setIsOpen(false);
    
    if (onSelectionChange) {
      onSelectionChange(new Set([value]));
    }
    
    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchQuery('');
    setSelected(null);
    setIsOpen(false);
    if (onSelectionChange) {
      onSelectionChange(new Set());
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const displayValue = selected ? selected.label : searchQuery;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={`w-full px-4 py-2 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all duration-200 bg-white ${props.disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
          disabled={props.disabled}
          {...(props.disabled ? {} : props)}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {selected && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 mr-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.map((option, index) => {
            const value = typeof option === 'string' ? option : (option.value || option.make || option);
            const label = typeof option === 'string' ? option : (option.label || option.make || option.value || option);
            const isSelected = selected && selected.value === value;
            
            return (
              <button
                key={`${value}-${index}`}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors ${
                  isSelected ? 'bg-[#DC2626] text-white' : 'text-gray-900'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {isOpen && searchQuery && filteredOptions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="px-4 py-2 text-gray-500 text-sm">No results found for "{searchQuery}"</div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;

